import { queriesPayrollRuns } from "@query";
import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import {
  ListPayrollRunsFilterParameter,
  PayrollRun
} from "@wingspanhq/wingspan-api-sdk";
import { useCallback, useState } from "react";

import { downloadCSV } from "../../Reports/utils/sheetjs/downloadCSV";
import { selectorPayrollRunCsvRow } from "../selectors/selectorPayrollRunCsvRow";

const CSV_PAGE_SIZE = 100;

/**
 * Exports the payroll history table. There is no run-level export endpoint on
 * v3, so the rows are paged out of `listPayrollRuns` with the table's own
 * filter and written client-side — the same approach as the invoices export.
 */
export const useDownloadPayrollRunsCsv = ({
  filter,
  filename
}: {
  filter: ListPayrollRunsFilterParameter;
  filename: string;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { openSnackbar } = useWSSnackbar();

  const downloadCsv = useCallback(async () => {
    try {
      setIsLoading(true);

      const payrollRuns: PayrollRun[] = [];
      let token: string | undefined = undefined;

      do {
        // `forceFetchQueryData`, not the cache-first `fetchQueryData`: an export
        // is an explicit "give me the data as it is now" action.
        const page: Awaited<
          ReturnType<
            typeof queriesPayrollRuns.listPayrollRuns.forceFetchQueryData
          >
        > = await queriesPayrollRuns.listPayrollRuns.forceFetchQueryData({
          filter,
          page: { size: CSV_PAGE_SIZE, token }
        });
        payrollRuns.push(...(page.data ?? []));
        token = page.pagination?.nextPageToken ?? undefined;
      } while (token);

      await downloadCSV(payrollRuns.map(selectorPayrollRunCsvRow), filename);

      openSnackbar({ type: "success", message: "CSV downloaded" });
    } catch {
      openSnackbar({ type: "warning", message: "Failed to download CSV" });
    } finally {
      setIsLoading(false);
    }
  }, [filter, filename, openSnackbar]);

  return { downloadCsv, isLoading };
};
